"use client";

import { useState, useEffect } from "react";
import {
  AdjustmentsHorizontalIcon,
  ArrowsUpDownIcon,
  Squares2X2Icon,
  ListBulletIcon,
  XMarkIcon,
  MapPinIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import SearchableSelect, {
  SearchableSelectOption,
} from "@/components/ui/SearchableSelect";
import { useMobileMenu } from "@/contexts/MobileMenuContext";

interface PriceRange {
  min: string;
  max: string;
}

interface MobileActionPanelProps {
  sortBy: string;
  viewMode: "grid" | "list";
  priceRange: PriceRange;
  selectedLocation: string;
  locationOptions: SearchableSelectOption[];
  onSortChange: (sort: string) => void;
  onViewModeChange: (mode: "grid" | "list") => void;
  onPriceRangeChange: (range: PriceRange) => void;
  onLocationSelect: (location: string) => void;
  /** Show a badge dot on the Filter button when panel filters are active (Phase 4). */
  hasActiveFilters?: boolean;
}

export default function MobileActionPanel({
  sortBy,
  viewMode,
  priceRange,
  selectedLocation,
  locationOptions,
  onSortChange,
  onViewModeChange,
  onPriceRangeChange,
  onLocationSelect,
  hasActiveFilters = false,
}: MobileActionPanelProps) {
  const [activePanel, setActivePanel] = useState<"sort" | "filter" | null>(
    null,
  );
  const [tempPriceRange, setTempPriceRange] = useState<PriceRange>(priceRange);
  const [priceValidationError, setPriceValidationError] = useState<
    string | null
  >(null);
  const { navMode } = useMobileMenu();

  const closePanel = () => {
    setActivePanel(null);
  };

  // Close the panel when the user clicks outside or presses escape
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && activePanel) {
        closePanel();
      }
    };

    window.addEventListener("keydown", handleEscKey);

    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [activePanel]);

  const togglePanel = (panel: "sort" | "filter") => {
    if (activePanel === panel) {
      setActivePanel(null);
    } else {
      setActivePanel(panel);
      if (panel === "filter") {
        setTempPriceRange(priceRange);
        setPriceValidationError(null);
      }
    }
  };

  const applyPriceFilter = () => {
    const minVal = tempPriceRange.min ? parseInt(tempPriceRange.min, 10) : null;
    const maxVal = tempPriceRange.max ? parseInt(tempPriceRange.max, 10) : null;
    if (minVal !== null && isNaN(minVal)) {
      setPriceValidationError("Min price must be a number");
      return;
    }
    if (maxVal !== null && isNaN(maxVal)) {
      setPriceValidationError("Max price must be a number");
      return;
    }
    if (minVal !== null && maxVal !== null && minVal > maxVal) {
      setPriceValidationError("Min must be less than or equal to max");
      return;
    }
    setPriceValidationError(null);
    onPriceRangeChange(tempPriceRange);
    closePanel();
  };

  const resetPriceFilter = () => {
    const resetRange = { min: "", max: "" };
    setTempPriceRange(resetRange);
    setPriceValidationError(null);
    onPriceRangeChange(resetRange);
    closePanel();
  };

  const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempPriceRange((prev) => ({ ...prev, min: value }));
    setPriceValidationError(null);
  };

  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempPriceRange((prev) => ({ ...prev, max: value }));
    setPriceValidationError(null);
  };

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {activePanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-foreground/50 z-20 lg:hidden"
            onClick={closePanel}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Fixed expandable bottom panel */}
      <div
        className={`fixed bottom-16 mb-2 left-1/2 transform -translate-x-1/2 lg:hidden z-30 transition-transform duration-300 ${
          navMode === "hidden" ? "translate-y-16" : "translate-y-0"
        }`}
      >
        <motion.div
          layout
          transition={{
            layout: { duration: 0.3, type: "spring", bounce: 0.2 },
          }}
          className={`bg-primary-800 mb-3 bg-opacity-90 rounded-md shadow-lg overflow-hidden ${activePanel ? "w-[90vw]" : ""}`}
        >
          {/* Panel Header */}
          <motion.div layout className="flex items-center justify-between">
            <button
              onClick={() => togglePanel("sort")}
              className={`flex items-center text-white px-4 py-2.5 ${activePanel === "sort" ? "font-medium bg-surface/10 text-primary" : ""}`}
              aria-label="Sort options"
            >
              <span className="mr-1.5 text-md">Sort</span>
              <ArrowsUpDownIcon className="h-3.5 w-3.5" />
            </button>

            <div className="h-5 w-px bg-border-strong mx-1"></div>

            <button
              onClick={() => togglePanel("filter")}
              className={`relative flex items-center text-white px-4 py-2.5 ${activePanel === "filter" ? "font-medium bg-surface/10 text-primary" : ""}`}
              aria-label={
                hasActiveFilters
                  ? "Filter options (filters active)"
                  : "Filter options"
              }
            >
              <span className="mr-1.5 text-md">Filter</span>
              <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
              {hasActiveFilters && (
                <span
                  className="absolute top-1.5 right-2 h-2 w-2 rounded-full bg-primary ring-2 ring-primary-800"
                  aria-hidden="true"
                />
              )}
            </button>

            {activePanel && (
              <button
                onClick={closePanel}
                className="text-white p-2 ml-auto"
                aria-label="Close panel"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </motion.div>

          {/* Expandable Content */}
          <AnimatePresence>
            {activePanel === "sort" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-surface rounded-b-md overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => {
                        onSortChange("proximity");
                        closePanel();
                      }}
                      className={`flex items-center p-3 rounded-lg text-sm ${
                        sortBy === "proximity"
                          ? "bg-primary-50 text-primary border border-primary-200"
                          : "bg-secondary text-foreground hover:bg-tertiary"
                      }`}
                    >
                      <MapPinIcon className="h-5 w-5 mr-3" />
                      <span>Location (Nearest)</span>
                    </button>

                    <button
                      onClick={() => {
                        onSortChange("newest");
                        closePanel();
                      }}
                      className={`flex items-center p-3 rounded-lg text-sm ${
                        sortBy === "newest"
                          ? "bg-primary-50 text-primary border border-primary-200"
                          : "bg-secondary text-foreground hover:bg-tertiary"
                      }`}
                    >
                      <ClockIcon className="h-5 w-5 mr-3" />
                      <span>Date (Newest First)</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {activePanel === "filter" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-surface rounded-b-md overflow-auto max-h-[60vh]"
              >
                <div className="p-4">
                  {/* Location Filter */}
                  <div className="mb-6">
                    <h4 className="text-md font-medium text-foreground mb-3">
                      Location
                    </h4>
                    <SearchableSelect
                      options={locationOptions}
                      value={selectedLocation}
                      onChange={onLocationSelect}
                      placeholder="Search locations..."
                      className="h-10"
                    />
                  </div>

                  {/* Price Filter */}
                  <div className="mb-6">
                    <h4 className="text-md font-medium text-foreground mb-3">
                      Price Range
                    </h4>
                    <div className="flex space-x-3">
                      <div className="w-1/2">
                        <label
                          htmlFor="minPrice"
                          className="block text-sm font-medium text-foreground mb-1"
                        >
                          Min Price (₦)
                        </label>
                        <input
                          id="minPrice"
                          type="number"
                          min={0}
                          inputMode="numeric"
                          className="w-full p-2 border border-border rounded-lg focus:ring-primary focus:border-primary"
                          placeholder="0"
                          value={tempPriceRange.min}
                          onChange={handleMinPriceChange}
                          aria-invalid={!!priceValidationError}
                        />
                      </div>
                      <div className="w-1/2">
                        <label
                          htmlFor="maxPrice"
                          className="block text-sm font-medium text-foreground mb-1"
                        >
                          Max Price (₦)
                        </label>
                        <input
                          id="maxPrice"
                          type="number"
                          min={0}
                          inputMode="numeric"
                          className="w-full p-2 border border-border rounded-lg focus:ring-primary focus:border-primary"
                          placeholder="Any"
                          value={tempPriceRange.max}
                          onChange={handleMaxPriceChange}
                          aria-invalid={!!priceValidationError}
                        />
                      </div>
                    </div>
                    {priceValidationError && (
                      <p className="mt-2 text-sm text-red-600" role="alert">
                        {priceValidationError}
                      </p>
                    )}
                  </div>

                  {/* View Mode Filter */}
                  <div className="mb-2">
                    <h4 className="text-md font-medium text-foreground mb-3">
                      View Mode
                    </h4>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => onViewModeChange("grid")}
                        className={`flex-1 py-2 px-4 rounded-md flex items-center justify-center ${
                          viewMode === "grid"
                            ? "bg-primary-50 text-primary border border-primary-200"
                            : "bg-secondary text-foreground border border-border hover:bg-tertiary"
                        }`}
                      >
                        <Squares2X2Icon className="h-5 w-5 mr-2" />
                        <span>Grid</span>
                      </button>

                      <button
                        onClick={() => onViewModeChange("list")}
                        className={`flex-1 py-2 px-4 rounded-md flex items-center justify-center ${
                          viewMode === "list"
                            ? "bg-primary-50 text-primary border border-primary-200"
                            : "bg-secondary text-foreground border border-border hover:bg-tertiary"
                        }`}
                      >
                        <ListBulletIcon className="h-5 w-5 mr-2" />
                        <span>List</span>
                      </button>
                    </div>
                  </div>

                  {/* Filter Action Buttons */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={resetPriceFilter}
                      className="w-1/2 py-2.5 px-4 border border-border rounded-lg text-foreground bg-surface hover:bg-secondary"
                    >
                      Reset
                    </button>
                    <button
                      onClick={applyPriceFilter}
                      className="w-1/2 py-2.5 px-4 rounded-lg bg-primary text-white hover:bg-primary-600"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Spacer while the action panel is visible (below lg) */}
      <div className="lg:hidden h-16"></div>
    </>
  );
}
